'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, MenuItem, useTheme } from '@mui/material';
import { ReactNode } from 'react';

import { NavLink } from './app-header-nav';
import styles from './app-header-nav-sm-up.module.scss';
import { getIsLinkTreeActive } from './use-app-header-nav-controller';

const SUBMENU_COLLAPSE_DURATION_MS = 180;
const ROOT_ITEM_HORIZONTAL_PADDING_SPACING = 3;
const SUBMENU_ITEM_LEFT_PADDING_SPACING = 5;
const SUBMENU_ITEM_RIGHT_PADDING_SPACING = 3;
const MENU_LEFT_ACCENT_PX = 4;

interface AppHeaderNavSmUpMenuItemsProps {
  links: NavLink[];
  openSubmenus: Set<string>;
  onLeafClick: (onClick?: () => void) => void;
  onSubmenuClick: (submenuKey: string, onClick?: () => void) => void;
  onSubmenuMouseEnter: (submenuKey: string, isLockedByActivePage: boolean) => void;
  onSubmenuMouseLeave: (submenuKey: string, isLockedByActivePage: boolean) => void;
  parentKey?: string;
  testId?: string;
}

export const AppHeaderNavSmUpMenuItems = ({
  links,
  onLeafClick,
  onSubmenuClick,
  onSubmenuMouseEnter,
  onSubmenuMouseLeave,
  openSubmenus,
  parentKey = 'root',
  testId,
}: Readonly<AppHeaderNavSmUpMenuItemsProps>) => {
  const theme = useTheme();
  const primaryMainColor = theme.palette.primary.main;
  const inactiveMenuItemColor = theme.palette.grey[500];

  const renderMenuItems = (nodes: NavLink[], depth = 0, keyPrefix = parentKey): ReactNode[] =>
    nodes.flatMap((link) => {
      const submenuKey = `${keyPrefix}-${link.label}`;
      const hasChildren = Boolean(link.children?.length);
      const isSubmenuOpen = openSubmenus.has(submenuKey);
      const isHighlighted = Boolean(link.isActive) || isSubmenuOpen;
      const submenuLockedByActivePage = hasChildren && getIsLinkTreeActive(link);
      const isNestedRow = depth > 0;
      const nestedRowCoverSx = isNestedRow
        ? {
            backgroundColor: theme.palette.background.paper,
            marginLeft: `-${MENU_LEFT_ACCENT_PX}px`,
            position: 'relative' as const,
            width: `calc(100% + ${MENU_LEFT_ACCENT_PX}px)`,
          }
        : {};

      const commonSx = {
        '&:hover': {
          backgroundColor: isNestedRow ? theme.palette.background.paper : undefined,
          color: primaryMainColor,
        },
        'color': isHighlighted ? primaryMainColor : inactiveMenuItemColor,
        'paddingBottom': theme.spacing(1),
        'paddingLeft':
          depth === 0
            ? theme.spacing(ROOT_ITEM_HORIZONTAL_PADDING_SPACING)
            : `calc(${theme.spacing(SUBMENU_ITEM_LEFT_PADDING_SPACING)} + ${MENU_LEFT_ACCENT_PX}px)`,
        'paddingRight': theme.spacing(SUBMENU_ITEM_RIGHT_PADDING_SPACING),
        'paddingTop': theme.spacing(1),
        ...nestedRowCoverSx,
      };

      if (!hasChildren) {
        return [
          <MenuItem
            className={styles.dropdownItem}
            data-testid={testId && `${testId}-navMenuItem-${link.label}`}
            disabled={Boolean(link.isDisabled && !link.isActive)}
            key={`${depth}-${link.label}`}
            onClick={() => onLeafClick(link.onClick)}
            sx={commonSx}
          >
            {link.label}
          </MenuItem>,
        ];
      }

      return [
        <Box
          key={`${submenuKey}-${depth}`}
          onMouseLeave={() => onSubmenuMouseLeave(submenuKey, submenuLockedByActivePage)}
        >
          <MenuItem
            className={styles.dropdownItem}
            data-testid={testId && `${testId}-navMenuGroup-${link.label}`}
            disabled={Boolean(link.isDisabled && !link.isActive)}
            onClick={() => onSubmenuClick(submenuKey, link.onClick)}
            onMouseEnter={() => onSubmenuMouseEnter(submenuKey, submenuLockedByActivePage)}
            sx={commonSx}
          >
            <Box
              alignItems="center"
              display="flex"
              gap={1}
              justifyContent="space-between"
              width="100%"
            >
              {link.label}
              {isSubmenuOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </Box>
          </MenuItem>
          <Collapse
            in={isSubmenuOpen}
            timeout={SUBMENU_COLLAPSE_DURATION_MS}
            unmountOnExit
            sx={{
              '& .MuiCollapse-wrapper': {
                backgroundColor: theme.palette.background.paper,
              },
              '& .MuiCollapse-wrapperInner': {
                backgroundColor: theme.palette.background.paper,
                display: 'block',
              },
              'backgroundColor': theme.palette.background.paper,
              'marginLeft': `-${MENU_LEFT_ACCENT_PX}px`,
              'width': `calc(100% + ${MENU_LEFT_ACCENT_PX}px)`,
            }}
          >
            {renderMenuItems(link.children!, depth + 1, submenuKey)}
          </Collapse>
        </Box>,
      ];
    });

  return <>{renderMenuItems(links)}</>;
};
